import React from 'react';
import {
  Action, ConfirmModal, FormSubmitType, HStack, Message, Portal, Spacing, Stack, Tooltip, useModal, useNotifications,
} from '@doveit/bricks';
import Divider from '../../../../components/divider/Divider';
import SelectContact, { OperationType } from '../../../../contact/containers/select-contact/SelectContact';
import NoContactView from '../../../../contact/containers/select-contact/components/no-contact-view/NoContactView';
import { Contact, OfferWizardData } from '../../../../providers/api/dtos';
import WizardCompanyForm, { WizardCompanyFormModel } from '../../../../company/components/wizard-company-form/WizardCompanyForm';
import { OfferWizardStepProps } from '../types';
import useContact from '../../../../contact/hooks/use-contact/useContact';
import { toAssignmentWizardSellerModel, toOfferWizardData } from './mapper';
import createOrUpdateContact from '../../../../contact/utils/createOrUpdateContact';
import { getContact } from '../../../../providers/api/contact/contactProvider';
import { ContactStatus } from '../../../../contact/containers/select-contact/types';
import { ContactState } from '../../../../contact/types';
import { mergeContact } from '../../../../contact/utils/mergeContact';
import { hasContactChanged } from '../../../../contact/containers/select-contact/utils/hasContactChanged';
import { wizardSellerSchema } from '../../../../property/assignment/components/assignment-wizard/assignment-wizard-seller/schema';
import { CONTACT_COMPARISON_KEYS } from '../../../../contact/constants';

export const NO_CONTACTS_INFO_MESSAGE = 'Aggiungi il/i compratore/i dell\'immobile.';
export const NO_LEGAL_INFO_MESSAGE = 'Aggiungi il rappresentante legale.';
export const LEGAL_PERSON_WITH_MANY_CONTACTS_TOOLTIP = 'Non è possibile indicare la tipologia Persona giuridica in presenza di più contatti.';
export const SOME_CONTACT_HAVE_BEEN_UPDATED_OUTSIDE_MESSAGE = "Dall'ultimo salvataggio, alcuni dati relativi ai compratori sono stati modificati. Verifica la loro correttezza.";

const OfferWizardBuyers: React.FC<OfferWizardStepProps> = ({
  intent,
  initialData,
  formRef,
  loading = false,
  onSubmit,
}) => {
  const { addSuccess, addError } = useNotifications();
  const { data: intentContact, isLoading: contactIntentLoading } = useContact(intent.contactId);

  const overwriteContactInfoModal = useModal();

  const [contacts, setContacts] = React.useState<ContactState[]>([]);
  const [isLegalPersonOnly, setIsLegalPersonOnly] = React.useState(initialData.isBuyerLegalPersonOnly || false);
  const [areContactsDirty, setAreContactsDirty] = React.useState(false);
  const [someContactsHaveBeenChangedOutside, setSomeContactsHaveBeenChangedOutside] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const isLegalPersonDisabled = React.useMemo(() => contacts.length > 1, [contacts.length]);

  React.useEffect(() => {
    async function loadBuyerContacts() {
      try {
        let contactsToShow: { contact?: Contact, overriddenFields?: Partial<Contact>, contactUpdatedAt?: string }[] = [];

        if (initialData.buyerContacts) {
          contactsToShow = await Promise.all(
            initialData.buyerContacts.map(async (contact) => {
              if (contact.id) {
                const dbContact = await getContact(contact.id);

                return {
                  contact: dbContact,
                  overriddenFields: contact.overriddenFields,
                  contactUpdatedAt: contact.updatedAt,
                };
              }

              return {
                overriddenFields: {
                  name: contact.name,
                  email: contact.email,
                  phoneNumber: contact.phoneNumber,
                  birthplace: contact.birthplace,
                  birthday: contact.birthday,
                  fiscalCode: contact.fiscalCode,
                  residence: contact.placeOfResidence,
                  identityDocument: contact.identityDocument,
                  citizenship: contact.citizenship,
                },
              };
            }),
          );

          const hasContactsBeenUpdatedExternally = contactsToShow.some(
            ({ contact, contactUpdatedAt, overriddenFields }) => {
              if (!contact?.updatedAt || !contactUpdatedAt) {
                return true;
              }

              return hasContactChanged(contact, overriddenFields ?? contact, [...CONTACT_COMPARISON_KEYS, 'identityDocument', 'citizenship']);
            },
          );

          setSomeContactsHaveBeenChangedOutside(hasContactsBeenUpdatedExternally);
        } else if (intentContact) {
          contactsToShow = [{ contact: intentContact }];
        }

        setContacts(
          contactsToShow.map(({ contact, overriddenFields }) => ({
            internalId: crypto.randomUUID(),
            status: 'OK' as ContactStatus,
            existingContact: contact,
            newContactValues: overriddenFields,
          })),
        );
      } catch (_) {
        addError('Errore nel caricamento dei contatti compratori.');
      }
    }

    loadBuyerContacts();
  }, [addError, initialData.buyerContacts, intentContact]);

  const handleContactsChange = React.useCallback((
    internalId: string,
    status: ContactStatus,
    existingContact?: Contact,
    newContactValues?: Partial<Contact>,
    operation?: OperationType,
  ) => {
    if (operation === 'DESELECT') {
      setContacts((prevContacts) => prevContacts.filter((contact) => contact.internalId !== internalId));
    } else if (contacts.some((contact) => contact.internalId === internalId)) {
      setContacts((prev) => prev.map((contact) => {
        if (contact.internalId !== internalId) return contact;

        return {
          ...contact, internalId, status, existingContact, newContactValues,
        } as ContactState;
      }));
    } else {
      setContacts((prev) => [...prev, {
        internalId, status, existingContact, newContactValues,
      } as ContactState]);
    }

    setAreContactsDirty(true);
  }, [contacts]);

  const updateContacts = React.useCallback(async () => {
    try {
      setIsSaving(true);

      const updatedContacts = await Promise.all(contacts
        .map((contactState) => createOrUpdateContact(contactState.newContactValues, contactState.existingContact).then((updatedContact) => ({
          ...contactState,
          existingContact: updatedContact,
          newContactValues: undefined,
        }))));

      addSuccess('I contatti compratori sono stati aggiornati con successo.');

      overwriteContactInfoModal.close();
      setIsSaving(false);
      setContacts(updatedContacts);
      setAreContactsDirty(true);
      setSomeContactsHaveBeenChangedOutside(false);
    } catch (_) {
      addError('Non è stato possibile salvare correttamente i contatti.');
      setIsSaving(false);
    }
  }, [addError, addSuccess, contacts, overwriteContactInfoModal]);

  const onPersonTypeChange = React.useCallback((isLegalPerson: boolean) => () => {
    setIsLegalPersonOnly(isLegalPerson);
  }, []);

  const onInternalSubmit: FormSubmitType<WizardCompanyFormModel> = React.useCallback(async (formValues: WizardCompanyFormModel) => {
    let savedContacts = contacts.map((c) => c.existingContact!);
    const isBuyerInfoFormDirty = (formRef?.current as any)?.dirty ?? false;
    const isLegalPersonOnlyDirty = initialData.isBuyerLegalPersonOnly !== isLegalPersonOnly;
    const dirty = isBuyerInfoFormDirty || areContactsDirty || isLegalPersonOnlyDirty;

    if (dirty) {
      const contactPromises: Promise<{ savedContact?: Contact, error?: Error, newContactValues?: Partial<Contact> }>[] = contacts
        .map(({ existingContact, newContactValues }) => createOrUpdateContact(newContactValues, existingContact)
          .then((savedContact) => ({ savedContact }))
          .catch((error: Error) => ({ error, newContactValues })));

      const results = await Promise.all(contactPromises);
      const errors = results.filter((result) => !!result.error);
      if (errors.length > 0) {
        errors.forEach((error) => addError(`Si è verificato un errore nel salvataggio del contatto ${error.newContactValues?.name || error.newContactValues?.email || error.newContactValues?.phoneNumber}. Correggi.`));
        return;
      }

      savedContacts = results.map((result) => result.savedContact!);
    }

    const values: OfferWizardData = await toOfferWizardData(initialData, formValues, savedContacts, isLegalPersonOnly);

    onSubmit(values, dirty);
  }, [addError, areContactsDirty, contacts, formRef, initialData, isLegalPersonOnly, onSubmit]);

  return (
    <>
      <Divider>
        Tipologia
      </Divider>
      <Spacing margin={[0, 0, 200]}>
        <HStack>
          <Action
            label="Persona fisica"
            aria-label="Selezione persona fisica"
            size="S"
            emphasis="high"
            color={!isLegalPersonOnly ? 'primary' : 'neutral'}
            onClick={onPersonTypeChange(false)}
          />
          {isLegalPersonDisabled && (
            <Tooltip content={LEGAL_PERSON_WITH_MANY_CONTACTS_TOOLTIP}>
              <span>
                &nbsp;&nbsp;&nbsp;
                <Action
                  label="Persona giuridica"
                  aria-label="Selezione persona giuridica"
                  size="S"
                  emphasis="high"
                  color={isLegalPersonOnly ? 'primary' : 'neutral'}
                  disabled
                />
              </span>
            </Tooltip>
          )}
          {!isLegalPersonDisabled && (
            <Action
              label="Persona giuridica"
              aria-label="Selezione persona giuridica"
              size="S"
              emphasis="high"
              color={isLegalPersonOnly ? 'primary' : 'neutral'}
              onClick={onPersonTypeChange(true)}
            />
          )}
        </HStack>
      </Spacing>

      <Divider>
        {isLegalPersonOnly ? 'Rappresentante legale' : 'Elenco compratori'}
      </Divider>

      {someContactsHaveBeenChangedOutside && !areContactsDirty && (
        <Spacing margin={[0, 0, 200]}>
          <Message
            type="warning"
            message={SOME_CONTACT_HAVE_BEEN_UPDATED_OUTSIDE_MESSAGE}
            boxed
            aria-label="Messaggio per informare l'utente che i dati del contatto sono stati sovrascritti esternamente"
            actions={[
              <Action
                label="Conferma dati inseriti"
                size="S"
                color="warning"
                emphasis="high"
                onClick={() => overwriteContactInfoModal.open()}
              />,
            ]}
          />
        </Spacing>
      )}

      {(!isLegalPersonOnly || contacts.length === 0) && (
        <SelectContact
          onContactChange={(status, existing, newValues, operation) => handleContactsChange(crypto.randomUUID(), status, existing, newValues, operation)}
          render={{
            contactSelectedView: ({ openContactFinder }) => (
              <NoContactView
                label="Cerca o aggiungi"
                openContactFinder={openContactFinder}
              />
            ),
          }}
        />
      )}

      {contacts.length === 0 && (
        <Spacing margin={[200, 0, 0]}>
          <Message
            type="info"
            message={isLegalPersonOnly ? NO_LEGAL_INFO_MESSAGE : NO_CONTACTS_INFO_MESSAGE}
          />
        </Spacing>
      )}

      {contacts.length > 0 && (
        <Spacing margin={[200, 0, 0]}>
          <Stack>
            {contacts.map(({ internalId, existingContact, newContactValues }) => (
              <SelectContact
                key={internalId}
                onContactChange={(status, existing, newValues, operation) => handleContactsChange(internalId, status, existing, newValues, operation)}
                showClearContact
                showBirthData
                initialContact={existingContact || mergeContact(existingContact, newContactValues) as Contact}
                initialOverwriteValues={contacts.find((c) => c.internalId === internalId)?.newContactValues}
                render={{ noContactView: false }}
                editContactRequiredFields={{
                  name: true,
                  email: true,
                  phoneNumber: true,
                  birthPlace: true,
                  birthDate: true,
                  fiscalCode: true,
                  address: true,
                  identityDocument: true,
                  citizenship: true,
                }}
                showIdentityDocument
                showCitizenship
                editContactSchema={wizardSellerSchema}
              />
            ))}
          </Stack>
        </Spacing>
      )}

      <div style={{ display: isLegalPersonOnly && contacts.length > 0 ? 'block' : 'none' }}>
        <Spacing margin={[200, 0, 0]}>
          <WizardCompanyForm
            initialValues={toAssignmentWizardSellerModel(initialData)}
            formRef={formRef}
            onSubmit={onInternalSubmit}
            disabled={contacts.length === 0}
            loading={loading || contactIntentLoading}
          />
        </Spacing>
      </div>
      <Portal>
        <ConfirmModal
          {...overwriteContactInfoModal}
          title="Modifica dati contatto"
          aria-label="Modale per confermare le modifiche ai dati del contatto"
          onAbort={() => overwriteContactInfoModal.close()}
          onConfirm={updateContacts}
          loading={isSaving}
        >
          Confermando l&apos;operazione, i dati inseriti in precedenza saranno confermati.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default OfferWizardBuyers;
